import { faChevronDown, faChevronRight } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type PropsWithChildren, useState } from 'react';
import { type ClassName, twx } from '@/utils/tailwind';
import { Card, type CardTone } from './Card';

export interface CollapsibleSectionProps extends PropsWithChildren {
  /** Required: the uppercase-tracking label shown in the section header. */
  title: string;
  /** Optional secondary line shown under the title only when collapsed. */
  collapsedSubtitle?: React.ReactNode;
  /** Optional count rendered on the right side of the header. */
  count?: number | string;
  /** Optional trailing action node (e.g. an "Add" button). Clicks are
   * stopped so they don't trigger the header toggle. */
  trailing?: React.ReactNode;
  /** Default-open / default-closed. Behaves as initial state — not
   * controlled, so the caller doesn't have to thread the toggle. */
  defaultOpen?: boolean;
  /** When set, behaves controlled — `open` is read from props and toggles
   * fire `onOpenChange`. */
  open?: boolean;
  onOpenChange?: (next: boolean) => void;
  /** Background tone of the outer card. Defaults to `focal` to match the
   * existing LogisticsGroup / EnRouteSection treatment. */
  tone?: CardTone;
  /** Removes the section's outer bg/border so the content reads as a bare
   * heading instead of a chip. Used for the Curate "Unscheduled" pane,
   * which sits on a white card already. */
  bare?: boolean;
  className?: ClassName;
}

/**
 * Card-shaped section with a chevron-toggle header and an animated-free
 * collapse of its content. Replaces inline state + chevron + count + body
 * markup that lived in LogisticsGroup, EnRouteSection, and the Curate
 * Unscheduled pane.
 */
export const CollapsibleSection = ({
  title,
  collapsedSubtitle,
  count,
  trailing,
  defaultOpen = true,
  open: controlledOpen,
  onOpenChange,
  tone = 'focal',
  bare = false,
  className,
  children,
}: CollapsibleSectionProps) => {
  const [localOpen, setLocalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : localOpen;
  const toggle = () => {
    const next = !isOpen;
    if (!isControlled) setLocalOpen(next);
    onOpenChange?.(next);
  };

  const header = (
    <div className="w-full flex items-center gap-2 px-3 py-2 hover:bg-content-focal transition-colors">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={isOpen}
        // The chevron must stay aligned with the *title row* regardless of
        // whether a subtitle is present. Earlier this used `items-center`
        // on the parent flex which centered the chevron against the
        // stacked title+subtitle block — the chevron then drifted
        // vertically when the subtitle appeared/disappeared on toggle.
        // Now: align to top, wrap the chevron in a fixed-height box that
        // matches the title row's leading so the icon's optical center
        // sits with the title's optical center, and put the subtitle on
        // its own row below.
        className="flex-1 min-w-0 flex flex-col items-stretch text-left"
      >
        <span className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-2.5 h-3 shrink-0">
            <FontAwesomeIcon
              icon={isOpen ? faChevronDown : faChevronRight}
              style={{ width: 9, height: 9 }}
              // text-content-subtle (~#9178be) — was content-disabled which
              // is so pale on white surfaces (Curate "Unscheduled" pane)
              // the chevron disappeared.
              className="text-content-subtle"
            />
          </span>
          <span className="text-xs font-semibold uppercase tracking-wider text-content-subtle leading-none">
            {title}
          </span>
        </span>
        {!isOpen && collapsedSubtitle && (
          // Indent matches the chevron-box width (10px) + the gap-2 (8px)
          // so the subtitle visually starts under the title's first letter.
          <p className="text-xs text-content-disabled truncate pl-[18px] mt-0.5">
            {collapsedSubtitle}
          </p>
        )}
      </button>
      {trailing && <div onClick={(e) => e.stopPropagation()}>{trailing}</div>}
      {count !== undefined && (
        <span className="text-xs text-content-disabled shrink-0">{count}</span>
      )}
    </div>
  );

  if (bare) {
    return (
      <div className={twx('w-full', className)}>
        {header}
        {isOpen && <div>{children}</div>}
      </div>
    );
  }

  return (
    <Card tone={tone} className={twx('mb-2', className)}>
      {header}
      {isOpen && <div className="border-t border-content-border">{children}</div>}
    </Card>
  );
};
